// Task Calendar — shared resource × day timeline for property and team views.
//
// Visual design adapted from the prototype on `nars/tasks-v2`
// (sticky property column, day grid, lane-allocated pills, popup
// detail card, drag-and-drop). The data however is real:
//   - tasks/work-orders are pulled from GET /api/v2/task-occurrences
//     for the visible date window
//   - guest/owner stays and maintenance blocks are pulled from
//     POST /api/v2/reservations/search (categories STA + OWN + BLK)
//
// Drag-and-drop persists schedule changes for one-off tasks and assignee
// changes in team mode. Recurring tasks prompt for single-occurrence vs
// all-future scope (virtual pills are materialized before updating).
//
// Empty-cell click seeds the shared WorkOrderContext with the clicked
// rental_id + scheduled_date and opens the existing #work-order-add
// modal (the same one used on the /operations/tasks page) so the user
// can quickly add a task at that slot.
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import UIkit from "uikit";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { useHistory, useLocation } from "react-router-dom";
import { AppContexts } from "../../../providers";
import Loading from "../../common/Loading";
import RentalsService from "../../../services/rentals.service";
import TaskOccurrencesService from "../../../services/taskOccurrences.service";
import TaskRecurrenceRuleService from "../../../services/taskRecurrenceRule.service";
import WorkOrderService from "../../../services/workorder.service";
import ReservationsService from "../../../services/reservations.service";
import WorkSchedulesService from "../../../services/work_schedules.service";
import TagGroupsService from "../../../services/tag-groups.service";
import WorkOrderAdd from "../work_order_add";
import WorkOrderDelete from "../work_order_delete";
import { buildVirtualOccurrenceWorkOrder } from "./calendarOccurrenceHelpers";
import {
  UNASSIGNED_RESOURCE_ID,
  groupActivitiesByResources,
  isActivityOverdue,
  normalizeActivityAssignees,
  replaceDraggedRecipient,
  teamResourceIdsForActivity,
} from "./calendarSchedulerUtils";
import { normalizeWorkScheduleRecipients } from "../../operations/teamRecipients";
import {
  buildCalendarSearch,
  calendarActivityKey,
  formatActivityTimeRange,
  initials,
  isBookingActivity,
  parseCalendarSearch,
  propertyReadinessForActivities,
} from "./calendarPresentationUtils";
import { tagsMatchGroup } from "../../../utils/tag-groups";
import {
  buildDragFutureSplitPayload,
  isRecurringWorkOrder,
  promptRecurringEditScope,
} from "../recurringScopePrompt";
import { openWorkOrderAddPanel } from "../workOrderAddPanel";
import {
  PRIORITY_OPTIONS,
  PRIORITY_LABELS,
  getPriorityLabel,
} from "../priorityOptions";
import { usePermissionsStore } from "../../permissions/permissions_store";

// ---- date helpers ----------------------------------------------------------
function ymdLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function asYMD(input) {
  if (!input) return null;
  const m = String(input).match(/^(\d{4}-\d{2}-\d{2})/);
  if (m) return m[1];
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  return ymdLocal(d);
}
function addDaysISO(dateStr, n) {
  const d = new Date(`${asYMD(dateStr)}T00:00:00`);
  d.setDate(d.getDate() + n);
  return ymdLocal(d);
}
function getDaysArray(startDate, daysCount) {
  const arr = [];
  const d = new Date(`${asYMD(startDate)}T00:00:00`);
  for (let i = 0; i < daysCount; i++) {
    arr.push(ymdLocal(d));
    d.setDate(d.getDate() + 1);
  }
  return arr;
}
function clamp(n, min, max) {
  return Math.max(min, Math.min(n, max));
}
function dayIndexFromClientX(e, rowEl, stickyWidth, dayWidth, dayCount) {
  const rect = rowEl.getBoundingClientRect();
  const x = e.clientX - rect.left - stickyWidth;
  const raw = Math.floor(x / dayWidth);
  return clamp(raw, 0, dayCount - 1);
}
function isAfternoon(s) {
  const d = new Date(s);
  return d.getHours() >= 12;
}
function isMorning(s) {
  const d = new Date(s);
  return d.getHours() < 12;
}
function clampPopupPosition(x, y, w = 360, h = 280, pad = 12) {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return {
    x: Math.max(pad, Math.min(x, vw - w - pad)),
    y: Math.max(pad, Math.min(y, vh - h - pad)),
  };
}
function popupPositionAwayFromRect(rect, w = 360, h = 280, gap = 14) {
  const pad = 12;
  const vw = window.innerWidth;
  const rightX = rect.right + gap;
  const leftX = rect.left - w - gap;
  let x = rightX + w <= vw - pad ? rightX : leftX;
  let y = rect.top - 8;

  if (x < pad) {
    x = clamp(rect.left, pad, vw - w - pad);
    y = rect.bottom + gap;
    if (y + h > window.innerHeight - pad) {
      y = rect.top - h - gap;
    }
  }

  return clampPopupPosition(x, y, w, h, pad);
}
function formatDateRange(start, end) {
  const sd = new Date(`${start}T00:00:00`);
  const ed = new Date(`${end}T00:00:00`);
  const sameMonth = sd.getMonth() === ed.getMonth();
  const sameYear = sd.getFullYear() === ed.getFullYear();
  const startStr = sd.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
  const endStr = ed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  return sameMonth && sameYear
    ? `${startStr} – ${ed.getDate()}, ${ed.getFullYear()}`
    : `${startStr} – ${endStr}`;
}

function titleizeValue(value) {
  return String(value || "")
    .replace(/[-_]+/g, " ")
    .split(" ")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function formatPopupDateTime(value) {
  if (!value) return "";
  const datePart = asYMD(value);
  if (!datePart) return "";
  const dateLabel = new Date(`${datePart}T00:00:00`).toLocaleDateString();
  const timeMatch = String(value).match(/[T\s](\d{2}:\d{2})(?::\d{2})?/);
  if (!timeMatch || timeMatch[1] === "00:00") return dateLabel;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return dateLabel;
  const timeLabel = parsed.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
  return `${dateLabel} ${timeLabel}`;
}

// ---- visual config ---------------------------------------------------------
const ACTIVITY_META = {
  guest: {
    className: "calendar-item-guest",
    label: "Guest",
    icon: "/images/guest-white.svg",
    popupIcon: "/images/guest-active.svg",
  },
  owner: {
    className: "calendar-item-owner",
    label: "Owner",
    icon: "/images/owner-white.svg",
    popupIcon: "/images/owner-active.svg",
  },
  block: {
    className: "calendar-item-block",
    label: "Maintenance Block",
    icon: "/images/maintenance-white.svg",
    popupIcon: "/images/maintenance-active.svg",
  },
  maintenance: {
    className: "calendar-item-maintenance",
    label: "Maintenance",
    icon: "/images/maintenance-white.svg",
    popupIcon: "/images/maintenance-active.svg",
  },
  housekeeping: {
    className: "calendar-item-housekeeping",
    label: "Clean",
    icon: "/images/housekeeping-white.svg",
    popupIcon: "/images/housekeeper-active.svg",
  },
  inspection: {
    className: "calendar-item-task",
    label: "Inspection",
    icon: "/images/task-white.svg",
    popupIcon: "/images/task.svg",
  },
  task: {
    className: "calendar-item-task",
    label: "Task",
    icon: "/images/task-white.svg",
    popupIcon: "/images/task.svg",
  },
};

const STICKY_COL_WIDTH = 260;
const DAY_COL_WIDTH = 142;
const COMPACT_STICKY_COL_WIDTH = 210;
const COMPACT_DAY_COL_WIDTH = 86;
const LANE_HEIGHT = 48;
const COMPACT_LANE_HEIGHT = 30;
// Minimum row height so the sticky property label + address are never clipped.
const MIN_PROPERTY_ROW_HEIGHT = 56;
const MIN_PROPERTY_ROW_HEIGHT_NO_ADDRESS = 40;
const MIN_TEAM_ROW_HEIGHT = 72;

function propertyRowMinHeight(home) {
  if (!home?.address) return MIN_PROPERTY_ROW_HEIGHT_NO_ADDRESS;
  const estLabelLines = Math.max(1, Math.ceil(String(home.label || "").length / 32));
  // Addresses are intentionally kept to one ellipsized line in the sticky
  // column so long street names do not inflate the whole calendar row.
  const estAddressLines = 1;
  const padding = 22;
  const labelLine = 19;
  const addressLine = 17;
  const gap = 2;
  const estimated =
    padding + estLabelLines * labelLine + gap + estAddressLines * addressLine;
  return Math.max(MIN_PROPERTY_ROW_HEIGHT, estimated);
}

const TYPE_LABELS = {
  clean: "Clean",
  inspection: "Inspection",
  maintenance: "Maintenance",
  task: "Task",
  guest: "Guest",
  owner: "Owner",
  block: "Maintenance Block",
};

const VIEW_MAP = {
  "3-day": 3,
  "7-day": 7,
  "14-day": 14,
  "30-day": 30,
};

// Map a backend work-order task_type to the calendar's visual activity
// type. Per spec the only canonical types are Cleaning / Inspection /
// Maintenance / Other, so anything that isn't cleaning/inspection/
// maintenance falls into the generic "task" lane.
function workOrderToActivityType(taskType) {
  switch ((taskType || "").toLowerCase()) {
    case "cleaning":
      return "housekeeping";
    case "inspection":
      return "inspection";
    case "maintenance":
    case "pool_service":
      return "maintenance";
    default:
      return "task";
  }
}

function normalizeWorkOrderBatch(woData) {
  if (Array.isArray(woData)) return woData;
  let paginated =
    woData?.paginated || woData?.workorders || woData?.data || [];
  if (typeof paginated === "string") {
    try {
      paginated = JSON.parse(paginated);
    } catch (_) {
      paginated = [];
    }
  }
  return Array.isArray(paginated) ? paginated : [];
}

function assigneeDisplayName(assignee) {
  if (!assignee) return "";
  if (assignee.assignee_name) return assignee.assignee_name;
  if (assignee.first_name || assignee.last_name) {
    return `${assignee.first_name || ""} ${assignee.last_name || ""}`.trim();
  }
  return assignee.name || assignee.email || "";
}

function pickAssigneeNames(wo) {
  const list = wo.work_order_assignees;
  if (!Array.isArray(list) || list.length === 0) return "";
  return list
    .filter((assignee) => assignee.assignment_role !== "owner")
    .map(assigneeDisplayName)
    .filter(Boolean)
    .join(", ");
}

function pickPropertyManagerName(wo) {
  const list = wo.work_order_assignees;
  if (!Array.isArray(list) || list.length === 0) return "";
  return assigneeDisplayName(
    list.find((assignee) => assignee.assignment_role === "owner")
  );
}

function isAssignableActivity(activity) {
  return activity && !isBookingActivity(activity);
}

function taskStatusLabel(status) {
  if (String(status || "").toLowerCase() === "started") return "In Progress";
  return titleizeValue(status || "new");
}

function taskStatusClass(status) {
  const value = String(status || "new").toLowerCase();
  return value === "started" ? "inprogress" : value;
}

function activityDateLabel(activity) {
  return asYMD(activity?.scheduledDate || activity?.date || activity?.checkIn);
}

function displayNamesForAssignees(assigners, teamResources) {
  return normalizeActivityAssignees({ assignees: assigners })
    .filter((assignee) => assignee.assignment_role !== "owner")
    .map((assignee) => {
      if (assignee.name) return assignee.name;
      if (assignee.type === "recipient") {
        return teamResources.find(
          (resource) => String(resource.id) === String(assignee.id),
        )?.label;
      }
      return "External vendor";
    })
    .filter(Boolean)
    .join(", ");
}

const DroppableCalendarCell = ({
  day,
  dayIndex,
  resourceId,
  laneCount,
  isWeekend,
  isToday,
  canManage,
}) => {
  const id = `calendar-cell:${resourceId}:${day}`;
  const { isOver, setNodeRef } = useDroppable({
    id,
    data: { day, resourceId },
    disabled: !canManage,
  });
  return (
    <div
      ref={setNodeRef}
      data-calendar-day={day}
      data-calendar-resource-id={resourceId}
      style={{
        gridColumn: dayIndex + 2,
        gridRow: `1 / span ${laneCount}`,
        cursor: canManage ? "pointer" : "default",
      }}
      className={[
        "calendar-cell",
        "calendar-content-cell",
        isWeekend ? "calendar-cell-weekend" : "",
        isToday ? "calendar-cell-today" : "",
        isOver ? "calendar-cell-drop-target" : "",
      ].join(" ")}
    />
  );
};

const DraggableCalendarItem = ({
  activity,
  resourceId,
  className,
  style,
  children,
  onMouseEnter,
  onMouseLeave,
  onClick,
  onKeyDown,
  title,
  canManage,
}) => {
  const draggable = canManage && isAssignableActivity(activity);
  const dragId = `calendar-drag:${calendarActivityKey(activity, resourceId)}`;
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: dragId,
      data: { activity, sourceResourceId: resourceId },
      disabled: !draggable,
    });
  return (
    <div
      ref={setNodeRef}
      className={`${className}${isDragging ? " is-dragging" : ""}`}
      style={{
        ...style,
        transform: CSS.Translate.toString(transform),
      }}
      title={title}
      role={draggable ? "button" : undefined}
      tabIndex={draggable ? 0 : undefined}
      aria-label={title}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
      onKeyDown={(event) => {
        onKeyDown?.(event);
        if (!event.defaultPrevented) listeners?.onKeyDown?.(event);
      }}
    >
      {children}
    </div>
  );
};

// ---- component -------------------------------------------------------------
const WorkOrderCalendar = ({ viewMode = "property" }) => {
  const isTeamView = viewMode === "team";
  const history = useHistory();
  const location = useLocation();
  const initialCalendarStateRef = useRef(parseCalendarSearch(location.search));
  const [rentals, setCalendarRentals] = useState([]);
  const workOrderContext = useContext(AppContexts.WorkOrderContext);
  const { canEdit, canView } = usePermissionsStore();
  const canManageWorkOrders = canEdit("operations.work_orders");
  const canReadReservations = ["messaging.guests", "messaging.messages", "operations.work_orders", "operations.inspections", "crm.contacts"].some(canView);
  const canManageTaskCalendar = canEdit("operations.calendar");

  const [activities, setActivities] = useState([]);
  const [propertyStatuses, setPropertyStatuses] = useState([]);
  const [unscheduledActivities, setUnscheduledActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [addModalMounted, setAddModalMounted] = useState(false);
  const [shouldOpenAddModal, setShouldOpenAddModal] = useState(false);
  const [activeView, setActiveView] = useState(
    initialCalendarStateRef.current.activeView,
  );
  const [density, setDensity] = useState(
    initialCalendarStateRef.current.density,
  );
  const [dateWindow, setDateWindow] = useState(() => ({
    start:
      initialCalendarStateRef.current.startDate || ymdLocal(new Date()),
    days: VIEW_MAP[initialCalendarStateRef.current.activeView] || 7,
  }));
  const [popupData, setPopupData] = useState(null);
  const [deletePreparingKey, setDeletePreparingKey] = useState(null);
  const [activeDrag, setActiveDrag] = useState(null);
  const [moveDialog, setMoveDialog] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [tagGroups, setTagGroups] = useState([]);
  const [criteria, setCriteria] = useState({
    type: "",
    priority: "",
    assignee: "",
    property: "",
    user: "",
    property_group: "",
    status: "",
    search_term: "",
    assignment: "",
    recurrence: "",
    hide_completed: false,
    hide_empty: true,
    show_bookings: true,
    ...initialCalendarStateRef.current.criteria,
  });
  const popupHoverTimerRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const loadRequestRef = useRef(0);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 8 },
    }),
    useSensor(KeyboardSensor),
  );

  const [filterDraft, setFilterDraft] = useState({
    type: "",
    priority: "",
    property: "",
    user: "",
    property_group: "",
    status: "",
    assignment: "",
    recurrence: "",
    hide_completed: false,
    hide_empty: true,
    show_bookings: true,
  });

  const isCompact = density === "collapsed";
  const stickyColWidth = isCompact
    ? COMPACT_STICKY_COL_WIDTH
    : STICKY_COL_WIDTH;
  const dayColWidth = isCompact
    ? COMPACT_DAY_COL_WIDTH
    : isTeamView
      ? 170
      : DAY_COL_WIDTH;
  const laneHeight = isCompact ? COMPACT_LANE_HEIGHT : LANE_HEIGHT;

  const clearPopupTimer = useCallback(() => {
    if (popupHoverTimerRef.current) {
      clearTimeout(popupHoverTimerRef.current);
      popupHoverTimerRef.current = null;
    }
  }, []);

  const handleCriteriaChange = useCallback((key, value) => {
    setCriteria((c) => ({ ...c, [key]: value }));
  }, []);

  useEffect(() => {
    setFilterDraft({
      type: criteria.type || "",
      priority: criteria.priority || "",
      property: criteria.property || "",
      user: criteria.user || "",
      property_group: criteria.property_group || "",
      status: criteria.status || "",
      assignment: criteria.assignment || "",
      recurrence: criteria.recurrence || "",
      hide_completed: !!criteria.hide_completed,
      hide_empty: !!criteria.hide_empty,
      show_bookings: criteria.show_bookings !== false,
    });
  }, [
    criteria.type,
    criteria.priority,
    criteria.property,
    criteria.user,
    criteria.property_group,
    criteria.status,
    criteria.assignment,
    criteria.recurrence,
    criteria.hide_completed,
    criteria.hide_empty,
    criteria.show_bookings,
  ]);

  const applyFilterDraft = () => {
    setCriteria((prev) => ({ ...prev, ...filterDraft }));
    if (filterDropdownRef.current) {
      UIkit.dropdown(filterDropdownRef.current).hide(false);
    }
  };

  const clearFilterDraft = () => {
    setFilterDraft({
      type: "",
      priority: "",
      property: "",
      user: "",
      property_group: "",
      status: "",
      assignment: "",
      recurrence: "",
      hide_completed: false,
      hide_empty: true,
      show_bookings: true,
    });
  };

  const clearAppliedFilters = () => {
    setCriteria((prev) => ({
      ...prev,
      type: "",
      priority: "",
      property: "",
      user: "",
      property_group: "",
      status: "",
      assignment: "",
      recurrence: "",
      hide_completed: false,
      hide_empty: true,
      show_bookings: true,
    }));
  };

  // homes derived from real rentals; preserves the v2 shape
  // ({ id, label, address }) so the rest of the layout code is unchanged.
  const homes = useMemo(() => {
    const list = rentals || [];
    return list.map((r) => ({
      id: String(r.rental_id),
      label: r.name || r.pms_id || `Rental ${r.rental_id}`,
      address: r.address || "",
      rental_id: r.rental_id,
      pms_id: r.pms_id,
      tags: r.tags || [],
    }));
  }, [rentals]);

  const propertyById = useMemo(() => {
    const map = {};
    for (const h of homes) map[h.id] = h;
    return map;
  }, [homes]);
  const propertyStatusByRentalId = useMemo(() => {
    const map = {};
    for (const status of propertyStatuses) {
      const key = String(status.rental_id || "");
      if (key && !map[key]) map[key] = status;
    }
    return map;
  }, [propertyStatuses]);

  const selectedPropertyLabel = criteria.property
    ? propertyById[String(criteria.property)]?.label || "All Properties"
    : "All Properties";
  const selectedTypeLabel = criteria.type
    ? TYPE_LABELS[criteria.type] || "All Types"
    : "All Types";
  const selectedPriorityLabel = criteria.priority
    ? PRIORITY_LABELS[criteria.priority] || "All Priorities"
    : "All Priorities";
  const visibleHomes = useMemo(() => {
    if (!criteria.property) return homes;
    return homes.filter((home) => String(home.id) === String(criteria.property));
  }, [homes, criteria.property]);

  const teamResources = useMemo(() => {
    const recipients = (teamMembers || [])
      .filter((member) => !member.deleted)
      .map((member) => ({
        id: String(member.recipient_id),
        recipient_id: Number(member.recipient_id),
        label:
          [member.first_name, member.last_name].filter(Boolean).join(" ") ||
          member.name ||
          member.email ||
          `Team member ${member.recipient_id}`,
        email: member.email || "",
        avatar:
          member.profile_image_url ||
          member.avatar_url ||
          member.image_url ||
          "",
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        id: UNASSIGNED_RESOURCE_ID,
        label: "Unassigned",
        email: "Tasks without a team member",
        avatar: "",
      },
      ...recipients,
    ];
  }, [teamMembers]);
  const selectedUserLabel = criteria.user
    ? teamResources.find(
        (resource) => String(resource.id) === String(criteria.user)
      )?.label || "All Users"
    : "All Users";
  const selectedPropertyGroupLabel = criteria.property_group
    ? tagGroups.find(
        (group) => String(group.id) === String(criteria.property_group)
      )?.label || "All Property Groups"
    : "All Property Groups";

  // ---- data load ----------------------------------------------------------
  const days = useMemo(
    () => getDaysArray(dateWindow.start, dateWindow.days),
    [dateWindow.start, dateWindow.days]
  );
  const windowStartISO = days[0];
  const windowEndISO = addDaysISO(days[0], days.length); // exclusive

  const load = useCallback(async () => {
    if (!windowStartISO || !windowEndISO) return;
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    setLoading(true);
    setLoadError("");
    try {
      const dateFrom = windowStartISO;
      const dateTo = addDaysISO(windowEndISO, -1);
      const [occResult, reservationResult, unscheduledResult] =
        await Promise.allSettled([
          TaskOccurrencesService.list({
            date_from: dateFrom,
            date_to: dateTo,
          }),
          canReadReservations ? ReservationsService.searchReservation({
            start_date: dateFrom,
            end_date: dateTo,
            preset: "staying-between",
            filters: "standard,owners,blocks",
            categories: ["STA", "OWN", "BLK", ""],
            page: 0,
            per_page: -1,
          }) : ReservationsService.getCalendarBlocks({ start_date: dateFrom, end_date: dateTo }),
          WorkOrderService.getWorkOrders(undefined, {
            page: 0,
            per: 1000,
            sort: "updated_at",
          }),
        ]);

      if (requestId !== loadRequestRef.current) return;

      const failedSources = [];
      if (occResult.status === "rejected") {
        failedSources.push("tasks");
        console.error("task-occurrences fetch failed", occResult.reason);
      }
      if (reservationResult.status === "rejected") {
        failedSources.push("bookings");
        console.error("reservations fetch failed", reservationResult.reason);
      }
      if (unscheduledResult.status === "rejected") {
        failedSources.push("unscheduled tasks");
        console.error("unscheduled work-orders fetch failed", unscheduledResult.reason);
      }
      if (failedSources.length) {
        setLoadError(
          `Some calendar data could not be loaded: ${failedSources.join(", ")}.`,
        );
      }

      const occRes =
        occResult.status === "fulfilled"
          ? occResult.value
          : { data: { occurrences: [] } };
      const occurrences = occRes?.data?.occurrences || [];
      setPropertyStatuses(
        Array.isArray(occRes?.data?.property_statuses)
          ? occRes.data.property_statuses
          : [],
      );
      const resRes =
        reservationResult.status === "fulfilled"
          ? reservationResult.value
          : { data: { reservations: [] } };

      const reservations =
        resRes?.data?.reservations ||
        resRes?.data?.data ||
        resRes?.data ||
        [];

      const reservationById = new Map(
        (Array.isArray(reservations) ? reservations : [])
          .map((reservation) => [String(reservation.guest_reservation_id), reservation])
      );

      const woActivities = (Array.isArray(occurrences) ? occurrences : [])
        .filter((occ) => !!occ.scheduled_date)
        .map((occ) => {
          const assignees = normalizeActivityAssignees({
            assignees:
              occ.kind === "concrete"
                ? occ.work_order_assignees
                : occ.assignees,
          });
          return {
            type: workOrderToActivityType(occ.task_type),
            homeId: String(occ.rental_id || ""),
            label: occ.summary || occ.task_type || "Task",
            date: String(occ.scheduled_date).slice(0, 10),
            scheduledDate: occ.scheduled_date,
            status: occ.kind === "virtual" ? "new" : occ.status,
            priority: occ.priority,
            assignee:
              occ.kind === "concrete"
                ? pickAssigneeNames({
                    work_order_assignees: occ.work_order_assignees,
                  })
                : (occ.assignees || [])
                    .map((assignee) => assignee.name || assignee.id)
                    .join(", "),
            propertyManager:
              occ.kind === "concrete"
                ? pickPropertyManagerName(occ)
                : "",
            assignees,
            unassigned: !assignees.some(
              (assignee) =>
                assignee.type === "recipient" &&
                assignee.assignment_role !== "owner"
            ),
            category: occ.category,
            taskType: occ.task_type,
            id: occ.work_order_id || occ.occurrence_key,
            taskId: occ.work_order_id || null,
            workOrderId: occ.work_order_id || null,
            guestReservationId: occ.guest_reservation_id || null,
            scheduledStartTime: occ.scheduled_start_time || null,
            scheduledEndTime: occ.scheduled_end_time || null,
            ticket_number: occ.ticket_number,
            isRecurring: !!occ.is_recurring,
            isVirtual: occ.kind === "virtual",
            kind: occ.kind,
            occurrenceKey: occ.occurrence_key,
            taskRecurrenceRuleId: occ.rule_id || null,
            taskSource:
              occ.task_source || (occ.is_recurring ? "recurrence" : null),
            guestReservation: occ.guest_reservation_id
              ? reservationById.get(String(occ.guest_reservation_id)) || null
              : null,
            hasSchedule: true,
          };
        });

      const resActivities = (Array.isArray(reservations) ? reservations : [])
        .filter((r) => r.rental_id && r.check_in && r.check_out)
        .filter((r) => ["STA", "OWN", "BLK", ""].includes(r.category))
        .map((r) => ({
          type:
            r.category === "OWN"
              ? "owner"
              : r.category === "BLK" || r.category === ""
                ? "block"
                : "guest",
          homeId: String(r.rental_id),
          label:
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            r.guest_name ||
            r.summary ||
            r.notes ||
            (r.category === "BLK" || r.category === ""
              ? "Maintenance block"
              : "Reservation"),
          checkIn: r.check_in,
          checkOut: r.check_out,
          guestName:
            `${r.first_name || ""} ${r.last_name || ""}`.trim() ||
            r.guest_name ||
            "",
          reservationId: r.reservation_id || r.guest_reservation_id,
          source: r.source,
          formatted_phone: r.formatted_phone,
        }));

      const today = ymdLocal(new Date());
      const nextActivities = [...woActivities, ...resActivities].map(
        (activity) => ({
          ...activity,
          overdue: isActivityOverdue(activity, today),
        }),
      );
      setActivities(nextActivities);

      const unscheduledRows =
        unscheduledResult.status === "fulfilled"
          ? normalizeWorkOrderBatch(unscheduledResult.value?.data)
          : [];
      setUnscheduledActivities(
        unscheduledRows
          .filter((wo) => !wo.scheduled_date && wo.task_type)
          .map((wo) => {
            const assignees = normalizeActivityAssignees({
              assignees: wo.work_order_assignees,
            });
            return {
              type: workOrderToActivityType(wo.task_type),
              homeId: String(wo.rental_id || ""),
              label: wo.summary || wo.task_type || "Task",
              date: null,
              scheduledDate: null,
              scheduledStartTime: null,
              scheduledEndTime: null,
              status: wo.status,
              priority: wo.priority,
              assignee: pickAssigneeNames(wo),
              assignees,
              unassigned: !assignees.some(
                (assignee) =>
                  assignee.type === "recipient" &&
                  assignee.assignment_role !== "owner",
              ),
              taskType: wo.task_type,
              id: wo.id,
              taskId: wo.id,
              workOrderId: wo.id,
              ticket_number: wo.ticket_number,
              isRecurring: !!wo.task_recurrence_rule_id,
              isVirtual: false,
              taskRecurrenceRuleId: wo.task_recurrence_rule_id || null,
              taskSource: wo.task_source || null,
              hasSchedule: false,
            };
          }),
      );
    } finally {
      if (requestId === loadRequestRef.current) setLoading(false);
    }
  }, [canReadReservations, windowStartISO, windowEndISO]);

  const applyDraggedTaskMove = useCallback(
    async (dragged, droppedDate, targetResourceId) => {
      if (!canManageTaskCalendar) return;
      const sourceResourceId = String(dragged.sourceResourceId || "");
      const targetId = String(targetResourceId || "");
      const assigneeChanged =
        isTeamView && sourceResourceId !== targetId;
      const movedAssigners = assigneeChanged
        ? replaceDraggedRecipient(
            dragged.assignees,
            sourceResourceId,
            targetId
          )
        : null;
      const schedulePayload = {
        scheduled_date: droppedDate,
        ...(movedAssigners ? { assigners: movedAssigners } : {}),
      };
      const patchLocalDate = () => {
        setActivities((prev) =>
          prev.map((item) =>
            item.id === dragged.id
              ? {
                  ...item,
                  date: droppedDate,
                  scheduledDate: droppedDate,
                  ...(movedAssigners
                    ? {
                        assignees: normalizeActivityAssignees({
                          assignees: movedAssigners,
                        }),
                        assignee: displayNamesForAssignees(
                          movedAssigners,
                          teamResources,
                        ),
                        unassigned: !movedAssigners.some(
                          (assignee) =>
                            assignee.type === "recipient" &&
                            assignee.assignment_role !== "owner",
                        ),
                      }
                    : {}),
                }
              : item
          )
        );
      };

      if (!dragged.isRecurring) {
        if (!dragged.workOrderId) return;
        patchLocalDate();
        try {
          if (dragged.hasSchedule === false) {
            await WorkOrderService.createSchedule(dragged.workOrderId, {
              ...schedulePayload,
              scheduled_start_time:
                dragged.scheduledStartTime || "09:00",
              scheduled_end_time: dragged.scheduledEndTime || "10:00",
            });
          } else {
            await WorkOrderService.updateSchedule(
              dragged.workOrderId,
              schedulePayload
            );
          }
          await load();
          UIkit.notification(
            dragged.hasSchedule === false
              ? "Task scheduled. Open it to adjust the time if needed."
              : "Task moved",
            "success",
          );
        } catch (err) {
          console.error("Failed to update task date", err);
          UIkit.notification(
            err?.response?.data?.conflicts?.[0] ||
              err?.response?.data?.error ||
              "Failed to move task",
            "danger"
          );
          load();
        }
        return;
      }

      const scope = await promptRecurringEditScope({
        itemLabel: "Task",
        summary: dragged.label,
        intent: "move",
      });
      if (!scope) return;

      const ruleId = dragged.taskRecurrenceRuleId;
      if (!ruleId) {
        UIkit.notification(
          "Unable to identify recurrence rule for this task.",
          "danger"
        );
        return;
      }

      if (scope === "this") {
        try {
          let workOrderId = dragged.workOrderId;
          if (dragged.isVirtual) {
            if (!dragged.occurrenceKey) {
              throw new Error("missing occurrence key");
            }
            const matRes = await TaskOccurrencesService.materialize({
              rule_id: ruleId,
              occurrence_key: dragged.occurrenceKey,
            });
            workOrderId = matRes?.data?.work_order?.id;
            if (!workOrderId) {
              throw new Error("materialize returned no task");
            }
          }
          await WorkOrderService.updateSchedule(workOrderId, schedulePayload);
          patchLocalDate();
          await load();
          UIkit.notification("Task moved", "success");
        } catch (err) {
          console.error("Failed to move recurring occurrence", err);
          UIkit.notification("Failed to move task", "danger");
          load();
        }
        return;
      }

      try {
        const ruleRes = await TaskRecurrenceRuleService.get(ruleId);
        const rule = ruleRes?.data?.rule || ruleRes?.data;
        const dragPayload =
          droppedDate === dragged.date
            ? {}
            : buildDragFutureSplitPayload(
                rule,
                dragged.date,
                droppedDate
              );
        if (assigneeChanged) {
          dragPayload.default_assignees = replaceDraggedRecipient(
            rule.default_assignees,
            sourceResourceId,
            targetId
          );
        }

        if (scope === "all") {
          await TaskRecurrenceRuleService.update(ruleId, {
            edit_mode: "all",
            ...dragPayload,
          });
          await load();
          UIkit.notification("All upcoming occurrences updated", "success");
          return;
        }

        await TaskRecurrenceRuleService.split(ruleId, {
          effective_date: dragged.date,
          payload: dragPayload,
        });
        await load();
        UIkit.notification("All future occurrences updated", "success");
      } catch (err) {
        console.error("Failed to move future occurrences", err);
        UIkit.notification(
          scope === "all"
            ? "Failed to update upcoming occurrences"
            : "Failed to update future occurrences",
          "danger"
        );
        load();
      }
    },
    [canManageTaskCalendar, isTeamView, load, teamResources]
  );

  useEffect(() => {
    document.title = `${
      isTeamView ? "Team Scheduler" : "Task Calendar"
    } - RueBaRue`;
  }, [isTeamView]);

  useEffect(() => {
    let active = true;
    Promise.all([
      WorkSchedulesService.listRecipientsWithSchedules({ page: 0, per_page: 500 }),
      TagGroupsService.list().catch(() => ({ data: [] })),
    ])
      .then(([teamResponse, groupResponse]) => {
        if (!active) return;
        setTeamMembers(normalizeWorkScheduleRecipients(teamResponse?.data));
        setTagGroups(
          Array.isArray(groupResponse?.data) ? groupResponse.data : []
        );
      })
      .catch((error) => {
        console.error("team scheduler resources fetch failed", error);
        UIkit.notification("Failed to load team members", "danger");
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const nextSearch = buildCalendarSearch({
      activeView,
      startDate: dateWindow.start,
      density,
      criteria,
    });
    if (nextSearch !== location.search) {
      history.replace({
        pathname: location.pathname,
        search: nextSearch,
      });
    }
  }, [
    activeView,
    criteria,
    dateWindow.start,
    density,
    history,
    location.pathname,
    location.search,
  ]);

  useEffect(() => {
    let active = true;
    RentalsService.getCalendarRentals().then((response) => {
      if (active) setCalendarRentals(response.data || []);
    }).catch(() => {
      if (active) UIkit.notification("Unable to load calendar properties", "danger");
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // popup dismissal
  useEffect(() => {
    if (!popupData) return undefined;
    const onKey = (e) => e.key === "Escape" && setPopupData(null);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [popupData]);

  // ensure timer is cleared on unmount
  useEffect(() => () => clearPopupTimer(), [clearPopupTimer]);

  // ---- filtering ----------------------------------------------------------
  const filteredActivities = useMemo(() => {
    let list = activities;
    const t = (criteria.type || "").trim();
    if (t) {
      list = list.filter((a) => {
        if (t === "clean") return a.type === "housekeeping";
        if (t === "task") return a.type === "task";
        if (t === "inspection") return a.type === "inspection";
        if (t === "maintenance") return a.type === "maintenance";
        if (t === "guest") return a.type === "guest";
        if (t === "owner") return a.type === "owner";
        if (t === "block") return a.type === "block";
        return true;
      });
    }
    if (criteria.property) {
      list = list.filter((a) => String(a.homeId) === String(criteria.property));
    }
    if (criteria.property_group) {
      list = list.filter((activity) =>
        tagsMatchGroup(
          propertyById[String(activity.homeId)]?.tags || [],
          tagGroups,
          criteria.property_group
        )
      );
    }
    if (criteria.status) {
      list = list.filter(
        (activity) =>
          String(activity.status || "new").toLowerCase() === criteria.status
      );
    }
    if (criteria.priority) {
      // reservations don't have priorities; only filter task-like activities
      list = list.filter((a) => {
        if (isBookingActivity(a)) return false;
        return (a.priority || "").toLowerCase() === criteria.priority;
      });
    }
    if (criteria.assignment === "unassigned") {
      list = list.filter(
        (activity) => isAssignableActivity(activity) && activity.unassigned,
      );
    }
    if (criteria.assignment === "assigned") {
      list = list.filter(
        (activity) => isAssignableActivity(activity) && !activity.unassigned,
      );
    }
    if (criteria.assignment === "vendor") {
      list = list.filter((activity) =>
        normalizeActivityAssignees(activity).some(
          (assignee) =>
            assignee.type === "contact" &&
            assignee.assignment_role !== "owner",
        ),
      );
    }
    if (criteria.recurrence === "recurring") {
      list = list.filter(
        (activity) => isAssignableActivity(activity) && activity.isRecurring,
      );
    }
    if (criteria.recurrence === "one-time") {
      list = list.filter(
        (activity) => isAssignableActivity(activity) && !activity.isRecurring,
      );
    }
    if (criteria.hide_completed) {
      list = list.filter(
        (activity) =>
          !["completed", "closed"].includes(
            String(activity.status || "").toLowerCase(),
          ),
      );
    }
    if (criteria.show_bookings === false) {
      list = list.filter((activity) => !isBookingActivity(activity));
    }
    if (criteria.assignee) {
      const q = criteria.assignee.toLowerCase();
      list = list.filter((a) => (a.assignee || "").toLowerCase().includes(q));
    }
    if (criteria.search_term) {
      const q = criteria.search_term.toLowerCase();
      list = list.filter(
        (a) =>
          (a.label || "").toLowerCase().includes(q) ||
          (propertyById[String(a.homeId)]?.label || "")
            .toLowerCase()
            .includes(q) ||
          (a.assignee || "").toLowerCase().includes(q) ||
          (a.guestName || "").toLowerCase().includes(q) ||
          String(a.reservationId || "")
            .toLowerCase()
            .includes(q) ||
          String(a.ticket_number || "")
            .toLowerCase()
            .includes(q)
      );
    }
    return list;
  }, [activities, criteria, propertyById, tagGroups]);

  const filteredUnscheduledActivities = useMemo(() => {
    let list = unscheduledActivities;
    if (criteria.property) {
      list = list.filter(
        (activity) => String(activity.homeId) === String(criteria.property),
      );
    }
    if (criteria.type) {
      const expectedType =
        {
          clean: "housekeeping",
          task: "task",
          inspection: "inspection",
          maintenance: "maintenance",
        }[criteria.type] || criteria.type;
      list = list.filter((activity) => activity.type === expectedType);
    }
    if (criteria.priority) {
      list = list.filter(
        (activity) =>
          String(activity.priority || "").toLowerCase() === criteria.priority,
      );
    }
    if (criteria.status) {
      list = list.filter(
        (activity) =>
          String(activity.status || "new").toLowerCase() === criteria.status,
      );
    }
    if (criteria.assignment === "assigned") {
      list = list.filter((activity) => !activity.unassigned);
    }
    if (criteria.assignment === "unassigned") {
      list = list.filter((activity) => activity.unassigned);
    }
    if (criteria.recurrence === "recurring") {
      list = list.filter((activity) => activity.isRecurring);
    }
    if (criteria.recurrence === "one-time") {
      list = list.filter((activity) => !activity.isRecurring);
    }
    if (criteria.hide_completed) {
      list = list.filter(
        (activity) =>
          !["completed", "closed"].includes(
            String(activity.status || "").toLowerCase(),
          ),
      );
    }
    if (criteria.search_term) {
      const query = criteria.search_term.toLowerCase();
      list = list.filter(
        (activity) =>
          String(activity.label || "").toLowerCase().includes(query) ||
          String(propertyById[String(activity.homeId)]?.label || "")
            .toLowerCase()
            .includes(query) ||
          String(activity.assignee || "").toLowerCase().includes(query) ||
          String(activity.ticket_number || "").toLowerCase().includes(query),
      );
    }
    return list;
  }, [criteria, propertyById, unscheduledActivities]);

  const visibleTeamResources = criteria.user
    ? teamResources.filter(
        (resource) => String(resource.id) === String(criteria.user)
      )
    : teamResources;
  const visibleResources = isTeamView
    ? visibleTeamResources
    : visibleHomes;
  const gridActivities = isTeamView
    ? filteredActivities.filter((activity) => !isBookingActivity(activity))
    : filteredActivities;
  const activitiesByResource = useMemo(
    () =>
      groupActivitiesByResources(
        visibleResources,
        gridActivities,
        isTeamView
          ? teamResourceIdsForActivity
          : (activity) => [String(activity.homeId)]
      ),
    [visibleResources, gridActivities, isTeamView]
  );
  const renderedResources = criteria.hide_empty
    ? visibleResources.filter(
        (resource) => (activitiesByResource[resource.id] || []).length > 0,
      )
    : visibleResources;

  const bookingContextByHomeDay = useMemo(() => {
    const map = {};
    activities
      .filter(isBookingActivity)
      .forEach((activity) => {
        const start = asYMD(activity.checkIn);
        const end = asYMD(activity.checkOut) || start;
        if (!start) return;
        let cursor = start;
        while (cursor && cursor <= end) {
          const key = `${activity.homeId}:${cursor}`;
          if (!map[key]) map[key] = [];
          map[key].push(activity);
          if (cursor === end) break;
          cursor = addDaysISO(cursor, 1);
        }
      });
    return map;
  }, [activities]);

  const calendarStats = useMemo(() => {
    const taskActivities = [
      ...activities.filter(isAssignableActivity),
      ...unscheduledActivities,
    ];
    const today = ymdLocal(new Date());
    return {
      unassigned: taskActivities.filter((activity) => activity.unassigned).length,
      overdue: taskActivities.filter((activity) => activity.overdue).length,
      dueToday: taskActivities.filter(
        (activity) => activityDateLabel(activity) === today,
      ).length,
      arrivals: activities.filter(
        (activity) =>
          activity.type === "guest" && asYMD(activity.checkIn) === today,
      ).length,
      departures: activities.filter(
        (activity) =>
          activity.type === "guest" && asYMD(activity.checkOut) === today,
      ).length,
      blocked: activities.filter(
        (activity) =>
          activity.type === "block" &&
          asYMD(activity.checkIn) <= today &&
          asYMD(activity.checkOut) >= today,
      ).length,
    };
  }, [activities, unscheduledActivities]);

  // ---- window helpers ----------------------------------------------------
  function overlapsWindow(inISO, outISO) {
    const start = inISO;
    const end = outISO || addDaysISO(inISO, 1);
    return !(end <= windowStartISO || start >= windowEndISO);
  }
  function indexInWindow(iso) {
    const i = days.indexOf(iso);
    if (i !== -1) return i;
    return iso < windowStartISO ? 0 : days.length;
  }
  function moveWindow(daysDelta) {
    setDateWindow((w) => ({ ...w, start: addDaysISO(w.start, daysDelta) }));
  }

  const openWorkOrderModal = () => {
    setAddModalMounted(true);
    setShouldOpenAddModal(true);
  };

  useEffect(() => {
    if (!shouldOpenAddModal || !addModalMounted) return;
    setShouldOpenAddModal(false);
    openWorkOrderAddPanel();
  }, [addModalMounted, shouldOpenAddModal]);

  // ---- empty-cell click -> open Add modal --------------------------------
  // We seed the shared WorkOrderContext with rental_id + scheduled_date
  // so the WorkOrderAdd form picks up the prefill; then we trigger the
  // existing UIkit modal.
  const openAddForCell = (rentalId, iso) => {
    if (!canManageWorkOrders) return;
    clearPopupTimer();
    setPopupData(null);
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(true);
    workOrderContext.setWorkOrder({
      account_id: 0,
      guest_reservation_id: 0,
      rental_id: Number(rentalId),
      scheduled_date: iso,
      message_id: 0,
      created_by: 0,
      ticket_number: 0,
      status: "new",
      priority: "medium",
      summary: "",
      description: "",
    });
    openWorkOrderModal();
  };

  const openAddTask = () => {
    if (!canManageWorkOrders) return;
    clearPopupTimer();
    setPopupData(null);
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(true);
    workOrderContext.initWorkOrder();
    openWorkOrderModal();
  };

  const handleEmptyRowClick = (home) => (e) => {
    if (!canManageTaskCalendar) return;
    if (activeDrag || e.defaultPrevented) return;
    if (
      e.target.closest(
        ".calendar-item, .calendar-cell-sticky, button, a, input, select, textarea"
      )
    ) {
      return;
    }

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (
      x < stickyColWidth ||
      x > stickyColWidth + days.length * dayColWidth
    ) {
      return;
    }

    const dayIndex = dayIndexFromClientX(
      e,
      e.currentTarget,
      stickyColWidth,
      dayColWidth,
      days.length
    );
    openAddForCell(home.rental_id, days[dayIndex]);
  };

  // ---- pill interactions -------------------------------------------------
  // Hover shows the read-only popup; click opens the edit modal.
  const handlePillHover = (activity, resourceId) => (e) => {
    if (activeDrag) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const { x, y } = popupPositionAwayFromRect(rect);
    clearPopupTimer();
    setPopupData({ activity, resourceId, position: { x, y } });
  };

  const handlePillLeave = () => {
    clearPopupTimer();
    popupHoverTimerRef.current = setTimeout(() => setPopupData(null), 120);
  };

  // Open the existing #work-order-add modal in edit mode for the
  // given activity (must be a task/work-order; reservations are skipped).
  const isRecurringActivity = (activity) =>
    Boolean(activity?.isRecurring) ||
    isRecurringWorkOrder({
      isVirtual: activity?.isVirtual,
      task_recurrence_rule_id: activity?.taskRecurrenceRuleId,
      task_source: activity?.taskSource,
    });

  const openEditForActivity = async (activity) => {
    if (!activity) return;
    if (isBookingActivity(activity)) return;
    if (!canManageWorkOrders) return;

    workOrderContext.setRecurringEditScope?.(null);
    if (isRecurringActivity(activity)) {
      const scope = await promptRecurringEditScope({
        itemLabel: "Task",
        summary: activity.label,
        intent: "open",
      });
      if (!scope) return;
      workOrderContext.setRecurringEditScope?.(scope);
    }

    workOrderContext.setIsNew(false);
    const occurrenceDate =
      asYMD(activity.scheduledDate) || asYMD(activity.date) || null;

    if (activity.isVirtual) {
      workOrderContext.setWorkOrder(
        buildVirtualOccurrenceWorkOrder(
          activity,
          occurrenceDate,
          activity.guestReservation || null,
        )
      );
      openWorkOrderModal();
      return;
    }

    if (!activity.workOrderId) return;

    workOrderContext.setWorkOrder((prev) => ({
      ...(prev || {}),
      id: activity.workOrderId,
      scheduled_date: occurrenceDate || prev?.scheduled_date || null,
      task_recurrence_rule_id: activity.taskRecurrenceRuleId || null,
      task_source: activity.taskSource || null,
    }));
    try {
      await workOrderContext.getWorkOrder(activity.workOrderId);
    } catch (_) {
      // Open anyway; the modal can still recover by re-fetching before submit.
    }
    openWorkOrderModal();
  };

  const openDeleteForActivity = async (activity) => {
    if (!activity || isBookingActivity(activity)) return;
    if (!canManageTaskCalendar) return;

    clearPopupTimer();
    setDeletePreparingKey(calendarActivityKey(activity));
    workOrderContext.invalidateWorkOrderFetch?.();
    workOrderContext.setRecurringEditScope?.(null);
    workOrderContext.setIsNew(false);

    const occurrenceDate =
      asYMD(activity.scheduledDate) || asYMD(activity.date) || null;

    try {
      if (activity.isVirtual) {
        workOrderContext.setWorkOrder(
          buildVirtualOccurrenceWorkOrder(
            activity,
            occurrenceDate,
            activity.guestReservation || null,
          ),
        );
      } else {
        if (!activity.workOrderId) {
          throw new Error("Task details are unavailable");
        }
        const loaded = await workOrderContext.getWorkOrder(activity.workOrderId);
        if (!loaded) throw new Error("Task details could not be loaded");
      }

      setPopupData(null);
      window.requestAnimationFrame(() => {
        const dialog = document.getElementById("work-order-delete");
        if (dialog) UIkit.modal(dialog).show();
      });
    } catch (_) {
      UIkit.notification("Failed to open task deletion", "danger");
    } finally {
      setDeletePreparingKey(null);
    }
  };

  const handlePillClick = (activity) => (e) => {
    e.stopPropagation();
    if (activeDrag) return;
    clearPopupTimer();
    setPopupData(null);
    void openEditForActivity(activity);
  };

  const openMoveDialog = (activity, sourceResourceId, presetDate = null) => {
    if (!canManageTaskCalendar) return;
    clearPopupTimer();
    setPopupData(null);
    setMoveDialog({
      activity,
      sourceResourceId: String(sourceResourceId || ""),
      date: presetDate || activityDateLabel(activity) || dateWindow.start,
      targetResourceId: isTeamView
        ? teamResources.some(
            (resource) => String(resource.id) === String(sourceResourceId),
          )
          ? String(sourceResourceId)
          : UNASSIGNED_RESOURCE_ID
        : String(activity.homeId || sourceResourceId || ""),
      startTime: activity.scheduledStartTime
        ? String(activity.scheduledStartTime).slice(0, 5)
        : "09:00",
      endTime: activity.scheduledEndTime
        ? String(activity.scheduledEndTime).slice(0, 5)
        : "10:00",
    });
  };

  const handleCalendarDragStart = ({ active }) => {
    if (!canManageTaskCalendar) return;
    const data = active?.data?.current;
    if (!data?.activity) return;
    clearPopupTimer();
    setPopupData(null);
    setActiveDrag({
      activity: data.activity,
      sourceResourceId: String(data.sourceResourceId || ""),
    });
    document.body.classList.add("dragging-activity");
  };

  const handleCalendarDragCancel = () => {
    setActiveDrag(null);
    document.body.classList.remove("dragging-activity");
  };

  const handleCalendarDragEnd = ({ active, over }) => {
    if (!canManageTaskCalendar) return;
    const dragData = active?.data?.current;
    const dropData = over?.data?.current;
    setActiveDrag(null);
    document.body.classList.remove("dragging-activity");
    if (!dragData?.activity || !dropData?.day || !dropData?.resourceId) {
      if (over) UIkit.notification("Choose a valid calendar cell.", "warning");
      return;
    }

    const dragged = {
      ...dragData.activity,
      sourceResourceId: String(dragData.sourceResourceId || ""),
    };
    const targetResourceId = String(dropData.resourceId);
    if (!isTeamView && String(dragged.homeId) !== targetResourceId) {
      UIkit.notification(
        "A task cannot be moved to another property from this view.",
        "warning",
      );
      return;
    }
    if (
      String(activityDateLabel(dragged) || "") === String(dropData.day) &&
      String(dragged.sourceResourceId) === targetResourceId
    ) {
      UIkit.notification("The task is already in that slot.", "primary");
      return;
    }

    if (dragged.hasSchedule === false) {
      openMoveDialog(dragged, dragged.sourceResourceId, dropData.day);
      setMoveDialog((prev) =>
        prev ? { ...prev, targetResourceId } : prev,
      );
      return;
    }
    void applyDraggedTaskMove(dragged, dropData.day, targetResourceId);
  };

  const submitMoveDialog = async (e) => {
    e.preventDefault();
    if (!canManageTaskCalendar) return;
    if (!moveDialog?.activity || !moveDialog.date) return;
    const dragged = {
      ...moveDialog.activity,
      sourceResourceId: moveDialog.sourceResourceId,
      scheduledStartTime: moveDialog.startTime,
      scheduledEndTime: moveDialog.endTime,
    };
    const targetResourceId = isTeamView
      ? moveDialog.targetResourceId
      : String(moveDialog.activity.homeId);
    setMoveDialog(null);
    await applyDraggedTaskMove(dragged, moveDialog.date, targetResourceId);
  };

  const renderActivityContent = (activity) => {
    const property = propertyById[String(activity.homeId)];
    const propertyLabel = property?.label || "Property";
    const timeRange = formatActivityTimeRange(activity);
    const dateKey = activityDateLabel(activity);
    const bookingContext =
      criteria.show_bookings === false
        ? []
        : bookingContextByHomeDay[`${activity.homeId}:${dateKey}`] || [];
    const hasArrival = bookingContext.some(
      (booking) =>
        booking.type === "guest" && asYMD(booking.checkIn) === dateKey,
    );
    const hasDeparture = bookingContext.some(
      (booking) =>
        booking.type === "guest" && asYMD(booking.checkOut) === dateKey,
    );
    const hasBlock = bookingContext.some(
      (booking) => booking.type === "block",
    );

    if (isCompact && isAssignableActivity(activity)) {
      return (
        <>
          <span className="calendar-compact-avatar" aria-hidden="true">
            {initials(isTeamView ? propertyLabel : ACTIVITY_META[activity.type]?.label)}
          </span>
          <span className="calendar-sr-only">
            {propertyLabel}, {activity.label}
          </span>
          {activity.isRecurring && (
            <span className="calendar-compact-corner" title="Recurring">
              ↻
            </span>
          )}
          {activity.unassigned && (
            <span className="calendar-compact-alert" title="Unassigned">
              ?
            </span>
          )}
        </>
      );
    }

    const primary = isBookingActivity(activity)
      ? activity.label
      : isTeamView
        ? propertyLabel
        : activity.label;
    const secondary = isBookingActivity(activity)
      ? `${new Date(activity.checkIn).toLocaleDateString()}–${new Date(
          activity.checkOut,
        ).toLocaleDateString()}`
      : isTeamView
        ? [activity.label, timeRange].filter(Boolean).join(" · ")
        : [timeRange, activity.assignee || "Unassigned"]
            .filter(Boolean)
            .join(" · ");

    return (
      <>
        <img
          src={ACTIVITY_META[activity.type]?.icon}
          alt=""
          className="calendar-item-icon"
        />
        <span className="calendar-item-copy">
          <span className="calendar-item-primary">{primary}</span>
          {secondary && (
            <span className="calendar-item-secondary">{secondary}</span>
          )}
        </span>
        <span className="calendar-item-badges">
          {activity.isRecurring && (
            <span className="calendar-card-badge" title="Recurring task">
              ↻
            </span>
          )}
          {activity.priority &&
            !["medium", "low"].includes(String(activity.priority)) && (
              <span
                className="calendar-card-badge calendar-card-priority"
                title={getPriorityLabel(activity.priority)}
              >
                P
              </span>
            )}
          {activity.unassigned && (
            <span
              className="calendar-card-badge calendar-card-unassigned"
              title="Unassigned"
            >
              ?
            </span>
          )}
          {activity.overdue && (
            <span
              className="calendar-card-badge calendar-card-overdue"
              title="Overdue"
            >
              !
            </span>
          )}
        </span>
        {isTeamView && !isBookingActivity(activity) && (
          <span className="calendar-booking-context" aria-label="Booking context">
            {hasArrival && <span title="Guest arrival">IN</span>}
            {hasDeparture && <span title="Guest departure">OUT</span>}
            {hasBlock && <span title="Maintenance block">BLOCK</span>}
          </span>
        )}
      </>
    );
  };

  if (!homes.length && loading) {
    return <Loading />;
  }

  return (
    <>
      <div className="calendar-wrapper">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">
            {isTeamView ? "Team Scheduler" : "Task Calendar"}
          </h1>
        </div>

        <div id="hms-main-body" className="calendar-content-container">
          <div className="hms-guest-btn calendar-add-task-btn">
            {canManageWorkOrders && (
              <button
                className="uk-button hms-btn hms-red-btn"
                type="button"
                onClick={openAddTask}
              >
                <span uk-icon="icon: plus; ratio: 0.7"></span> Task
              </button>
            )}
            <div
              className="rbr-pill-toggle calendar-mode-toggle calendar-icon-toggle"
              role="group"
              aria-label="Calendar organization"
            >
              <button
                className={`rbr-pill-toggle-button${
                  !isTeamView ? " is-active" : ""
                }`}
                type="button"
                aria-label="Property view"
                aria-pressed={!isTeamView}
                uk-tooltip="Property view"
                onClick={() =>
                  history.push({
                    pathname: "/operations/calendar",
                    search: location.search,
                  })
                }
              >
                <span uk-icon="icon: home; ratio: 0.82" aria-hidden="true" />
              </button>
              <button
                className={`rbr-pill-toggle-button${
                  isTeamView ? " is-active" : ""
                }`}
                type="button"
                aria-label="Team view"
                aria-pressed={isTeamView}
                uk-tooltip="Team view"
                onClick={() =>
                  history.push({
                    pathname: "/operations/team-scheduler",
                    search: location.search,
                  })
                }
              >
                <span uk-icon="icon: users; ratio: 0.82" aria-hidden="true" />
              </button>
            </div>
            <div
              className="rbr-pill-toggle calendar-density-toggle calendar-icon-toggle"
              role="group"
              aria-label="Calendar density"
            >
              <button
                className={`rbr-pill-toggle-button${
                  !isCompact ? " is-active" : ""
                }`}
                type="button"
                aria-label="Detailed view"
                aria-pressed={!isCompact}
                uk-tooltip="Detailed view"
                onClick={() => setDensity("expanded")}
              >
                <span uk-icon="icon: list; ratio: 0.82" aria-hidden="true" />
              </button>
              <button
                className={`rbr-pill-toggle-button${
                  isCompact ? " is-active" : ""
                }`}
                type="button"
                aria-label="Collapsed view"
                aria-pressed={isCompact}
                uk-tooltip="Collapsed view"
                onClick={() => setDensity("collapsed")}
              >
                <span
                  uk-icon="icon: thumbnails; ratio: 0.82"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>

          <form className="uk-form hms-form workorder-form-top-filters calendar-toolbar-form">
            <div className="workorder-form-top-filters calendar-toolbar">
              <div className="hms-form hms-form-inline calendar-toolbar-main">
                <div className="uk-inline hms-search-inline calendar-search-inline">
                  <div className="uk-search uk-search-default">
                    {criteria.search_term ? (
                      <button
                        type="button"
                        aria-label="Clear search"
                        className="uk-form-icon-flip uk-form-icon"
                        uk-icon="icon: close"
                        onClick={() => handleCriteriaChange("search_term", "")}
                        style={{ background: "transparent", border: 0 }}
                      />
                    ) : (
                      <span
                        className="uk-search-icon-flip"
                        uk-search-icon="true"
                      />
                    )}
                    <input
                      className="uk-search-input"
                      type="search"
                      placeholder="Task, property, person, guest or #"
                      value={criteria.search_term}
                      onChange={(e) =>
                        handleCriteriaChange("search_term", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div className="hms-filters-btn calendar-filters-btn">
                  <button className="filter" type="button">
                    <img src="/images/filter.svg" alt="Filter" uk-tooltip="Filter" />
                    <span uk-icon="icon: filter; ratio: 0.9"></span>
                  </button>

                  <div
                    ref={filterDropdownRef}
                    uk-dropdown="mode: click; offset: 0; animation: uk-animation-slide-top-small; duration: 400"
                    className="hms-filters-dropdown calendar-filters-dropdown"
                  >
                    <div className="uk-form-stacked">
                      <div className="uk-margin-small">
                        <label className="uk-form-label">Type</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.type}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({ ...prev, type: e.target.value }))
                            }
                          >
                            <option value="">All Types</option>
                            <option value="clean">Cleaning</option>
                            <option value="inspection">Inspection</option>
                            <option value="maintenance">Maintenance</option>
                            <option value="task">Other</option>
                            {!isTeamView && (
                              <option value="guest">Guest</option>
                            )}
                            {!isTeamView && (
                              <option value="owner">Owner</option>
                            )}
                            {!isTeamView && (
                              <option value="block">Maintenance Block</option>
                            )}
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Priority</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.priority}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                priority: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Priorities</option>
                            {PRIORITY_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Property</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.property}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                property: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Properties</option>
                            {homes.map((h) => (
                              <option key={h.id} value={h.id}>
                                {h.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {isTeamView && (
                        <div className="uk-margin-small">
                          <label className="uk-form-label">User</label>
                          <div className="uk-form-controls">
                            <select
                              className="uk-select"
                              value={filterDraft.user}
                              onChange={(e) =>
                                setFilterDraft((prev) => ({
                                  ...prev,
                                  user: e.target.value,
                                }))
                              }
                            >
                              <option value="">All Users</option>
                              {teamResources.map((member) => (
                                <option key={member.id} value={member.id}>
                                  {member.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Property Group</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.property_group}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                property_group: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Property Groups</option>
                            {tagGroups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Status</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.status}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                status: e.target.value,
                              }))
                            }
                          >
                            <option value="">All Statuses</option>
                            <option value="new">New</option>
                            <option value="started">In Progress</option>
                            <option value="completed">Completed</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Assignment</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.assignment}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                assignment: e.target.value,
                              }))
                            }
                          >
                            <option value="">Any assignment</option>
                            <option value="assigned">Assigned</option>
                            <option value="unassigned">Unassigned</option>
                            <option value="vendor">Vendor assigned</option>
                          </select>
                        </div>
                      </div>

                      <div className="uk-margin-small">
                        <label className="uk-form-label">Schedule</label>
                        <div className="uk-form-controls">
                          <select
                            className="uk-select"
                            value={filterDraft.recurrence}
                            onChange={(e) =>
                              setFilterDraft((prev) => ({
                                ...prev,
                                recurrence: e.target.value,
                              }))
                            }
                          >
                            <option value="">One-time and recurring</option>
                            <option value="one-time">One-time only</option>
                            <option value="recurring">Recurring only</option>
                          </select>
                        </div>
                      </div>

                      <label className="calendar-filter-check">
                        <input
                          type="checkbox"
                          checked={filterDraft.hide_completed}
                          onChange={(e) =>
                            setFilterDraft((prev) => ({
                              ...prev,
                              hide_completed: e.target.checked,
                            }))
                          }
                        />
                        Hide completed
                      </label>
                      <label className="calendar-filter-check">
                        <input
                          type="checkbox"
                          checked={filterDraft.hide_empty}
                          onChange={(e) =>
                            setFilterDraft((prev) => ({
                              ...prev,
                              hide_empty: e.target.checked,
                            }))
                          }
                        />
                        Hide empty rows
                      </label>
                      <label className="calendar-filter-check">
                        <input
                          type="checkbox"
                          checked={filterDraft.show_bookings}
                          onChange={(e) =>
                            setFilterDraft((prev) => ({
                              ...prev,
                              show_bookings: e.target.checked,
                            }))
                          }
                        />
                        Show booking context
                      </label>

                      <div className="hms-filter-actions uk-margin-small-top">
                        <button
                          type="button"
                          className="uk-button hms-btn hms-red-btn"
                          onClick={applyFilterDraft}
                        >
                          Apply
                        </button>
                        <button
                          type="button"
                          className="uk-button hms-btn hms-white-btn"
                          onClick={clearFilterDraft}
                        >
                          Clear
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="calendar-date-controls">
                  <button
                    className="uk-button hms-btn hms-white-btn calendar-today-btn"
                    type="button"
                    onClick={() =>
                      setDateWindow((window) => ({
                        ...window,
                        start: ymdLocal(new Date()),
                      }))
                    }
                  >
                    Today
                  </button>
                  <div
                    className="rbr-pill-toggle calendar-view-toggle"
                    role="group"
                    aria-label="Calendar range"
                  >
                    {Object.keys(VIEW_MAP).map((label) => (
                      <button
                        key={label}
                        className={`rbr-pill-toggle-button${activeView === label ? " is-active" : ""}`}
                        type="button"
                        aria-pressed={activeView === label}
                        onClick={() => {
                          setActiveView(label);
                          setDateWindow((w) => ({
                            start: w.start,
                            days: VIEW_MAP[label],
                          }));
                        }}
                      >
                        {{
                          "3-day": "3 Days",
                          "7-day": "Week",
                          "14-day": "2 Weeks",
                          "30-day": "Month",
                        }[label] || label}
                      </button>
                    ))}
                  </div>

                  <div className="rbr-date-navigation calendar-date-nav">
                    <button
                      className="rbr-date-shift"
                      onClick={() => moveWindow(-dateWindow.days)}
                      type="button"
                      aria-label="Previous date range"
                      uk-tooltip="Previous date range"
                    >
                      <span uk-icon="chevron-left"></span>
                    </button>

                    <div
                      className="rbr-date-label calendar-date-range"
                      aria-live="polite"
                      aria-atomic="true"
                    >
                      <span
                        className="rbr-date-calendar"
                        uk-icon="icon: calendar; ratio: 0.9"
                      />
                      <span>
                        {formatDateRange(
                          dateWindow.start,
                          addDaysISO(dateWindow.start, dateWindow.days - 1)
                          )}
                      </span>
                      <input
                        className="calendar-date-picker-input"
                        type="date"
                        aria-label="Choose calendar start date"
                        value={dateWindow.start}
                        onChange={(e) =>
                          setDateWindow((window) => ({
                            ...window,
                            start: e.target.value || window.start,
                          }))
                        }
                      />
                    </div>

                    <button
                      className="rbr-date-shift"
                      onClick={() => moveWindow(dateWindow.days)}
                      type="button"
                      aria-label="Next date range"
                      uk-tooltip="Next date range"
                    >
                      <span uk-icon="chevron-right"></span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>

          {(criteria.type ||
            criteria.priority ||
            criteria.property ||
            criteria.user ||
            criteria.property_group ||
            criteria.status ||
            criteria.assignment ||
            criteria.recurrence ||
            criteria.hide_completed ||
            criteria.hide_empty ||
            criteria.show_bookings === false) && (
            <div className="hms-active-filters calendar-active-filters uk-margin-small-bottom">
              <span className="hms-active-filters-label">Filters:</span>
              {criteria.type && (
                <span className="hms-filter-chip">
                  Type: {selectedTypeLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("type", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.priority && (
                <span className="hms-filter-chip">
                  Priority: {selectedPriorityLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("priority", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.property && (
                <span className="hms-filter-chip">
                  Property: {selectedPropertyLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("property", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.user && (
                <span className="hms-filter-chip">
                  User: {selectedUserLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("user", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.property_group && (
                <span className="hms-filter-chip">
                  Property Group: {selectedPropertyGroupLabel}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() =>
                      handleCriteriaChange("property_group", "")
                    }
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.status && (
                <span className="hms-filter-chip">
                  Status: {titleizeValue(criteria.status)}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("status", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.assignment && (
                <span className="hms-filter-chip">
                  Assignment: {titleizeValue(criteria.assignment)}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("assignment", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.recurrence && (
                <span className="hms-filter-chip">
                  Schedule: {titleizeValue(criteria.recurrence)}
                  <button
                    type="button"
                    className="hms-filter-chip-close"
                    onClick={() => handleCriteriaChange("recurrence", "")}
                  >
                    ×
                  </button>
                </span>
              )}
              {criteria.hide_completed && (
                <span className="hms-filter-chip">Hide completed</span>
              )}
              {criteria.hide_empty && (
                <span className="hms-filter-chip">Hide empty rows</span>
              )}
              {criteria.show_bookings === false && (
                <span className="hms-filter-chip">Bookings hidden</span>
              )}
              <button
                type="button"
                className="hms-filter-chip-clear-all"
                onClick={clearAppliedFilters}
              >
                Clear all
              </button>
            </div>
          )}

          {loadError && (
            <div className="calendar-data-warning" role="alert">
              <span uk-icon="icon: warning; ratio: 0.8" />
              {loadError}
              <button type="button" onClick={load}>Retry</button>
            </div>
          )}

          <div className="calendar-summary-strip" aria-label="Operations summary">
            <button type="button" onClick={() => handleCriteriaChange("assignment", "unassigned")}>
              <strong>{calendarStats.unassigned}</strong><span>Unassigned</span>
            </button>
            <div><strong>{calendarStats.overdue}</strong><span>Overdue</span></div>
            <div><strong>{calendarStats.dueToday}</strong><span>Due today</span></div>
            <div><strong>{calendarStats.arrivals}</strong><span>Arrivals</span></div>
            <div><strong>{calendarStats.departures}</strong><span>Departures</span></div>
            <div><strong>{calendarStats.blocked}</strong><span>Blocked</span></div>
          </div>

          <div className="calendar-legend" aria-label="Calendar legend">
            {Object.entries(ACTIVITY_META).map(([type, meta]) => (
              <span key={type}>
                <i className={meta.className} />{meta.label}
              </span>
            ))}
            <span><b>↻</b> Recurring</span>
            <span><b>?</b> Unassigned</span>
            <span><b>!</b> Overdue</span>
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleCalendarDragStart}
            onDragCancel={handleCalendarDragCancel}
            onDragEnd={handleCalendarDragEnd}
          >
            {filteredUnscheduledActivities.length > 0 && (
              <details className="calendar-unscheduled-tray">
                <summary>
                  Unscheduled tasks{" "}
                  <span>{filteredUnscheduledActivities.length}</span>
                </summary>
                <div className="calendar-unscheduled-list">
                  {filteredUnscheduledActivities.map((activity) => (
                    <DraggableCalendarItem
                      key={calendarActivityKey(activity, "unscheduled")}
                      activity={activity}
                      canManage={canManageTaskCalendar}
                      resourceId={
                        isTeamView
                          ? teamResourceIdsForActivity(activity)[0]
                          : "unscheduled"
                      }
                      className={[
                        "calendar-unscheduled-card",
                        ACTIVITY_META[activity.type]?.className,
                      ].join(" ")}
                      style={{}}
                      title={`Unscheduled: ${activity.label} · ${
                        propertyById[activity.homeId]?.label || "Property"
                      }`}
                      onClick={handlePillClick(activity)}
                      onKeyDown={(e) => {
                        if (e.key.toLowerCase() === "m") {
                          e.preventDefault();
                          openMoveDialog(
                            activity,
                            isTeamView
                              ? teamResourceIdsForActivity(activity)[0]
                              : "unscheduled",
                          );
                        }
                      }}
                    >
                      {renderActivityContent(activity)}
                    </DraggableCalendarItem>
                  ))}
                </div>
              </details>
            )}

            <div className="calendar-scroll">
            <div
              className={[
                "calendar-container",
                `calendar-density-${density}`,
                isTeamView ? "calendar-team-view" : "calendar-property-view",
              ].join(" ")}
              style={{
                width: `${stickyColWidth + days.length * dayColWidth}px`,
              }}
            >
              <div
                className="calendar-row calendar-header"
                style={{
                  gridTemplateColumns: `${stickyColWidth}px repeat(${days.length}, ${dayColWidth}px)`,
                }}
              >
                <div
                  className="calendar-cell calendar-cell-sticky"
                  style={{ display: "flex", alignItems: "center", fontWeight: 600 }}
                >
                  {isTeamView ? "Team member" : "Property"}
                </div>
                {days.map((d) => {
                  const isToday = d === ymdLocal(new Date());
                  return (
                    <div
                      key={d}
                      className={`calendar-cell calendar-header-cell${
                        isToday ? " calendar-cell-today" : ""
                      }`}
                    >
                      <div>
                        {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
                          weekday: "short",
                        })}
                      </div>
                      <div>
                        {new Date(`${d}T00:00:00`).toLocaleDateString(undefined, {
                          month: "short",
                          day: "numeric",
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              {renderedResources.map((home) => {
                const items = activitiesByResource[home.id] || [];
                const lanesProbe = [];
                const probe = (start, end) => {
                  for (let laneIndex = 0; ; laneIndex += 1) {
                    if (!lanesProbe[laneIndex]) {
                      lanesProbe[laneIndex] = Array(days.length).fill(false);
                    }
                    let available = true;
                    for (let d = start; d < end; d += 1) {
                      if (lanesProbe[laneIndex][d]) {
                        available = false;
                        break;
                      }
                    }
                    if (available) {
                      for (let d = start; d < end; d += 1) {
                        lanesProbe[laneIndex][d] = true;
                      }
                      return laneIndex;
                    }
                  }
                };
                const placements = items
                  .map((activity) => {
                    const inISO = activity.checkIn
                      ? asYMD(activity.checkIn)
                      : asYMD(activity.date);
                    const outISO = activity.checkOut
                      ? asYMD(activity.checkOut)
                      : null;
                    if (!inISO || !overlapsWindow(inISO, outISO)) return null;
                    let s = indexInWindow(inISO);
                    let e = outISO ? indexInWindow(outISO) + 1 : s + 1;
                    if (e <= s) e = s + 1;
                    s = Math.max(0, Math.min(s, days.length));
                    e = Math.max(0, Math.min(e, days.length));
                    const lane = probe(s, e);
                    return { activity, s, e, lane, inISO, outISO };
                  })
                  .filter(Boolean);
                const compactStackCounts = {};
                placements.forEach((placement) => {
                  if (!isCompact || !isAssignableActivity(placement.activity)) {
                    return;
                  }
                  placement.compactStackIndex =
                    compactStackCounts[placement.s] || 0;
                  compactStackCounts[placement.s] =
                    placement.compactStackIndex + 1;
                });
                placements.forEach((placement) => {
                  if (placement.compactStackIndex === undefined) return;
                  const total = compactStackCounts[placement.s] || 1;
                  const availableWidth = Math.max(0, dayColWidth - 32);
                  const step =
                    total > 1
                      ? Math.min(18, availableWidth / (total - 1))
                      : 0;
                  placement.compactOffset =
                    4 + placement.compactStackIndex * step;
                });
                const compactHasBookings = placements.some(({ activity }) =>
                  isBookingActivity(activity),
                );
                const compactHasTasks = placements.some(({ activity }) =>
                  isAssignableActivity(activity),
                );
                const compactTaskRow =
                  compactHasBookings && compactHasTasks ? 2 : 1;
                const laneCount = isCompact
                  ? compactHasBookings && compactHasTasks
                    ? 2
                    : 1
                  : Math.max(1, lanesProbe.length || 1);
                const minRowHeight = isCompact
                  ? 38
                  : isTeamView
                    ? MIN_TEAM_ROW_HEIGHT
                    : propertyRowMinHeight(home);
                const taskAreaHeight = laneCount * laneHeight;
                const rowHeight = Math.max(taskAreaHeight, minRowHeight);
                const readiness = !isTeamView
                  ? propertyReadinessForActivities(
                      home.id,
                      activities,
                      ymdLocal(new Date()),
                      propertyStatusByRentalId[String(home.id)] || null,
                    )
                  : null;

                return (
                  <div
                    key={home.id}
                    className="calendar-row"
                    style={{
                      gridTemplateColumns: `${stickyColWidth}px repeat(${days.length}, ${dayColWidth}px)`,
                      gridTemplateRows: `repeat(${laneCount}, ${Math.max(
                        laneHeight,
                        rowHeight / laneCount,
                      )}px)`,
                      minHeight: rowHeight,
                      "--lanes": laneCount,
                      "--sticky": `${stickyColWidth}px`,
                      "--day": `${dayColWidth}px`,
                      position: "relative",
                    }}
                    onClick={isTeamView ? undefined : handleEmptyRowClick(home)}
                  >
                    <div
                      className="calendar-cell calendar-cell-sticky"
                      style={{ gridRow: `1 / span ${laneCount}` }}
                      title={
                        home.address
                          ? `${home.label} — ${home.address}`
                          : home.email
                            ? `${home.label} — ${home.email}`
                            : home.label
                      }
                    >
                      {isTeamView ? (
                        <div className="calendar-team-resource">
                          {home.avatar ? (
                            <img
                              className="calendar-team-resource-avatar"
                              src={home.avatar}
                              alt=""
                            />
                          ) : (
                            <span
                              className={`calendar-team-resource-avatar calendar-team-resource-initials${
                                home.id === UNASSIGNED_RESOURCE_ID
                                  ? " is-unassigned"
                                  : ""
                              }`}
                              aria-hidden="true"
                            >
                              {home.id === UNASSIGNED_RESOURCE_ID
                                ? "?"
                                : initials(home.label)}
                            </span>
                          )}
                          <span className="calendar-team-resource-copy">
                            <strong>{home.label}</strong>
                            {!isCompact && home.email && (
                              <span>{home.email}</span>
                            )}
                          </span>
                        </div>
                      ) : isCompact ? (
                        <div className="calendar-resource-compact">
                          <span>{initials(home.label)}</span>
                          <strong>{home.label}</strong>
                        </div>
                      ) : (
                        <>
                          <div className="prop-id">{home.label}</div>
                          {(home.address || home.email) && (
                            <div className="secondary prop-address">
                              {home.address || home.email}
                            </div>
                          )}
                          {readiness && (
                            <span className={`calendar-readiness ${readiness.state}`}>
                              {readiness.label}
                            </span>
                          )}
                        </>
                      )}
                    </div>

                    {days.map((day, dayIndex) => (
                      <DroppableCalendarCell
                        key={day}
                        day={day}
                        dayIndex={dayIndex}
                        resourceId={home.id}
                        laneCount={laneCount}
                        isWeekend={[0, 6].includes(
                          new Date(`${day}T00:00:00`).getDay(),
                        )}
                        isToday={day === ymdLocal(new Date())}
                        canManage={canManageTaskCalendar}
                      />
                    ))}

                    {placements.map(({
                      activity,
                      s,
                      e,
                      lane,
                      inISO,
                      outISO,
                      compactOffset,
                    }) => {
                      const startsInsideWindow =
                        inISO >= windowStartISO && inISO < windowEndISO;
                      const endsInsideWindow =
                        outISO &&
                        outISO > windowStartISO &&
                        outISO < windowEndISO;
                      const halfEligible = [
                        "guest",
                        "owner",
                        "block",
                        "housekeeping",
                      ].includes(activity.type);
                      const title = [
                        isTeamView
                          ? propertyById[activity.homeId]?.label
                          : activity.label,
                        isTeamView ? activity.label : null,
                        formatActivityTimeRange(activity),
                        activity.isRecurring ? "Recurring" : "One-time",
                        activity.unassigned ? "Unassigned" : activity.assignee,
                      ]
                        .filter(Boolean)
                        .join(" · ");
                      return (
                        <DraggableCalendarItem
                          key={calendarActivityKey(activity, home.id)}
                          activity={activity}
                          canManage={canManageTaskCalendar}
                          resourceId={home.id}
                          className={[
                            "calendar-item",
                            ACTIVITY_META[activity.type]?.className,
                            `calendar-status-${taskStatusClass(activity.status)}`,
                            activity.unassigned ? "calendar-item-unassigned" : "",
                            activity.overdue ? "calendar-item-overdue" : "",
                            halfEligible &&
                            startsInsideWindow &&
                            activity.checkIn &&
                            isAfternoon(activity.checkIn)
                              ? "half-start"
                              : "",
                            halfEligible &&
                            endsInsideWindow &&
                            activity.checkOut &&
                            isMorning(activity.checkOut)
                              ? "half-end"
                              : "",
                          ].join(" ")}
                          style={{
                            gridColumn: `${s + 2} / ${e + 2}`,
                            gridRow: isCompact
                              ? isBookingActivity(activity)
                                ? 1
                                : compactTaskRow
                              : lane + 1,
                            "--compact-offset": `${compactOffset || 4}px`,
                          }}
                          title={title}
                          onMouseEnter={handlePillHover(activity, home.id)}
                          onMouseLeave={handlePillLeave}
                          onClick={handlePillClick(activity)}
                          onKeyDown={(event) => {
                            if (event.key.toLowerCase() === "m") {
                              event.preventDefault();
                              event.stopPropagation();
                              openMoveDialog(activity, home.id);
                            }
                          }}
                        >
                          {renderActivityContent(activity)}
                        </DraggableCalendarItem>
                      );
                    })}
                  </div>
                );
              })}

              {!renderedResources.length && !loading && (
                <div className="calendar-empty-state">
                  No calendar rows match the selected filters.
                </div>
              )}

            {popupData && (
              <div
                className="calendar-popup"
                onMouseEnter={clearPopupTimer}
                onMouseLeave={handlePillLeave}
                style={{
                  position: "fixed",
                  top: popupData.position.y,
                  left: popupData.position.x,
                  zIndex: 1100,
                }}
              >
                <button
                  type="button"
                  className="calendar-popup-close"
                  onClick={() => setPopupData(null)}
                  aria-label="Close"
                >
                  <img
                    src="/images/close.svg"
                    width="16"
                    height="16"
                    alt="Close"
                  />
                </button>

                <div className="calendar-popup-header">
                  <div className="calendar-popup-icon">
                    <img
                      src={
                        ACTIVITY_META[popupData.activity.type]?.popupIcon ||
                        ACTIVITY_META[popupData.activity.type]?.icon
                      }
                      alt={popupData.activity.type}
                      width="24"
                      height="24"
                    />
                  </div>
                  <div className="calendar-popup-title-block">
                    <div className="calendar-popup-type">
                      {ACTIVITY_META[popupData.activity.type]?.label || "Item"}
                    </div>
                    <h5>{popupData.activity.label}</h5>
                  </div>
                </div>

                {isBookingActivity(popupData.activity) && (
                  <div className="calendar-popup-details">
                    {popupData.activity.guestName && (
                      <div className="calendar-popup-row">
                        <span>Name</span>
                        <strong>{popupData.activity.guestName}</strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>
                        {popupData.activity.type === "block"
                          ? "Starts / Ends"
                          : "Check-in / Check-out"}
                      </span>
                      <strong>
                        {new Date(popupData.activity.checkIn).toLocaleDateString()} -{" "}
                        {new Date(popupData.activity.checkOut).toLocaleDateString()}
                      </strong>
                    </div>
                    <div className="calendar-popup-row">
                      <span>Property</span>
                      <strong>{propertyById[popupData.activity.homeId]?.label || ""}</strong>
                    </div>
                    {propertyById[popupData.activity.homeId]?.address && (
                      <div className="calendar-popup-row">
                        <span>Address</span>
                        <strong>{propertyById[popupData.activity.homeId].address}</strong>
                      </div>
                    )}
                    {popupData.activity.reservationId && (
                      <div className="calendar-popup-row">
                        <span>Reservation #</span>
                        <strong>{popupData.activity.reservationId}</strong>
                      </div>
                    )}
                    {popupData.activity.source && (
                      <div className="calendar-popup-row">
                        <span>Source</span>
                        <strong>{popupData.activity.source}</strong>
                      </div>
                    )}
                    {popupData.activity.formatted_phone && (
                      <div className="calendar-popup-row">
                        <span>Phone</span>
                        <strong>{popupData.activity.formatted_phone}</strong>
                      </div>
                    )}
                  </div>
                )}

                {!isBookingActivity(popupData.activity) && (
                  <div className="calendar-popup-details">
                    <div className="calendar-popup-row">
                      <span>Summary</span>
                      <strong>{popupData.activity.label}</strong>
                    </div>
                    {popupData.activity.date && (
                      <div className="calendar-popup-row">
                        <span>Scheduled</span>
                        <strong>
                          {formatPopupDateTime(
                            popupData.activity.scheduledDate || popupData.activity.date
                          )}
                          {formatActivityTimeRange(popupData.activity)
                            ? ` · ${formatActivityTimeRange(popupData.activity)}`
                            : ""}
                        </strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>Property</span>
                      <strong>{propertyById[popupData.activity.homeId]?.label || ""}</strong>
                    </div>
                    {propertyById[popupData.activity.homeId]?.address && (
                      <div className="calendar-popup-row">
                        <span>Address</span>
                        <strong>{propertyById[popupData.activity.homeId].address}</strong>
                      </div>
                    )}
                    {popupData.activity.status && (
                      <div className="calendar-popup-row">
                        <span>Status</span>
                        <strong>{taskStatusLabel(popupData.activity.status)}</strong>
                      </div>
                    )}
                    {popupData.activity.priority && (
                      <div className="calendar-popup-row">
                        <span>Priority</span>
                        <strong>{getPriorityLabel(popupData.activity.priority)}</strong>
                      </div>
                    )}
                    {popupData.activity.propertyManager && (
                      <div className="calendar-popup-row">
                        <span>Property Manager</span>
                        <strong>{popupData.activity.propertyManager}</strong>
                      </div>
                    )}
                    <div className="calendar-popup-row">
                      <span>Assigned To</span>
                      <strong>{popupData.activity.assignee || "Unassigned"}</strong>
                    </div>
                    <div className="calendar-popup-row">
                      <span>Schedule type</span>
                      <strong>
                        {popupData.activity.isRecurring ? "Recurring" : "One-time"}
                      </strong>
                    </div>
                    {popupData.activity.ticket_number && (
                      <div className="calendar-popup-row">
                        <span>#</span>
                        <strong>{popupData.activity.ticket_number}</strong>
                      </div>
                    )}
                    {canManageTaskCalendar && (
                      <div className="calendar-popup-actions">
                      <button
                        type="button"
                        className="uk-button hms-btn hms-white-btn calendar-popup-delete-button"
                        disabled={
                          deletePreparingKey ===
                          calendarActivityKey(popupData.activity)
                        }
                        onClick={() => {
                          void openDeleteForActivity(popupData.activity);
                        }}
                      >
                        {deletePreparingKey ===
                        calendarActivityKey(popupData.activity) ? (
                          <span data-uk-spinner="ratio: 0.45" />
                        ) : (
                          "Delete task"
                        )}
                      </button>
                      <button
                        type="button"
                        className="uk-button hms-btn hms-white-btn"
                        onClick={() => {
                          const { activity, resourceId } = popupData;
                          setPopupData(null);
                          openMoveDialog(activity, resourceId);
                        }}
                      >
                        Move / reassign
                      </button>
                      <button
                        type="button"
                        className="uk-button hms-btn hms-red-btn"
                        onClick={() => {
                          const { activity } = popupData;
                          setPopupData(null);
                          void openEditForActivity(activity);
                        }}
                      >
                        Open task
                      </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

            <DragOverlay>
              {activeDrag?.activity ? (
                <div
                  className={[
                    "calendar-item",
                    "calendar-drag-overlay",
                    ACTIVITY_META[activeDrag.activity.type]?.className,
                  ].join(" ")}
                >
                  {renderActivityContent(activeDrag.activity)}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>

          {canManageTaskCalendar && moveDialog && (
            <div
              className="calendar-move-backdrop"
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setMoveDialog(null);
              }}
            >
              <form
                className="calendar-move-dialog"
                onSubmit={submitMoveDialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby="calendar-move-title"
              >
                <div className="calendar-move-header">
                  <div>
                    <span>Schedule task</span>
                    <h3 id="calendar-move-title">
                      {moveDialog.activity.label}
                    </h3>
                  </div>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setMoveDialog(null)}
                  >
                    ×
                  </button>
                </div>
                <div className="calendar-move-property">
                  <span>Property</span>
                  <strong>
                    {propertyById[String(moveDialog.activity.homeId)]?.label ||
                      "Property"}
                  </strong>
                </div>
                <label>
                  Date
                  <input
                    className="uk-input"
                    type="date"
                    required
                    value={moveDialog.date}
                    onChange={(event) =>
                      setMoveDialog((dialog) => ({
                        ...dialog,
                        date: event.target.value,
                      }))
                    }
                  />
                </label>
                <div className="calendar-move-time-fields">
                  <label>
                    Start time
                    <input
                      className="uk-input"
                      type="time"
                      required
                      value={moveDialog.startTime}
                      onChange={(event) =>
                        setMoveDialog((dialog) => ({
                          ...dialog,
                          startTime: event.target.value,
                        }))
                      }
                    />
                  </label>
                  <label>
                    End time
                    <input
                      className="uk-input"
                      type="time"
                      required
                      min={moveDialog.startTime}
                      value={moveDialog.endTime}
                      onChange={(event) =>
                        setMoveDialog((dialog) => ({
                          ...dialog,
                          endTime: event.target.value,
                        }))
                      }
                    />
                  </label>
                </div>
                {isTeamView && (
                  <label>
                    Assign to
                    <select
                      className="uk-select"
                      value={moveDialog.targetResourceId}
                      onChange={(event) =>
                        setMoveDialog((dialog) => ({
                          ...dialog,
                          targetResourceId: event.target.value,
                        }))
                      }
                    >
                      {teamResources.map((resource) => (
                        <option key={resource.id} value={resource.id}>
                          {resource.label}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                <p className="calendar-move-help">
                  Drag a card for a quick change, or use this dialog for exact
                  date, time, and assignment control.
                </p>
                <div className="calendar-move-actions">
                  <button
                    type="button"
                    className="uk-button hms-btn hms-white-btn"
                    onClick={() => setMoveDialog(null)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="uk-button hms-btn hms-red-btn"
                  >
                    Save schedule
                  </button>
                </div>
              </form>
            </div>
          )}

        {loading && (
          <Loading />
        )}
        </div>
      </div>

      {/* Keep this mounted like /operations/tasks so UIkit, nested modals,
          antd dropdowns, and portals initialize against a stable offcanvas. */}
      <WorkOrderAdd
        showTaskType
        showRecurrence
        itemLabel="Task"
        taskTypeLabel="Type*"
        onSaved={load}
      />
      <WorkOrderDelete
        itemLabel="Task"
        closeEditOnDelete
        onDeleted={load}
      />
    </>
  );
};

export default WorkOrderCalendar;
